import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SellerLayout } from '../components/SellerLayout';
import { ShipLabelModal } from '../components/ShipLabelModal';
import { ShipTrackingModal } from '../components/ShipTrackingModal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  getOrder,
  downloadPickList,
  cancelSale,
} from '../api/client';
import { colors } from '../constants/colors';
import { parseUTC } from '../utils/date';
import { Package, ArrowLeft, Clock, Check, Truck, MapPin, FileText, Loader2, Tag, X, AlertTriangle } from 'lucide-react';
import type { Order, OrderStatus, ItemSaleWithPayment, Shipment, ShipmentStatus } from '../types';
import type { LucideIcon } from 'lucide-react';
import { Img } from '../components/Img';

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bgColor: string; Icon: LucideIcon }> = {
  OPEN: { label: 'Open', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  UNSHIPPED: { label: 'Unshipped', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  PARTIALLY_SHIPPED: { label: 'Partially Shipped', color: '#F97316', bgColor: '#FFF7ED', Icon: Package },
  LABEL_CREATED: { label: 'Label Created', color: '#5856D6', bgColor: '#EDE9FE', Icon: Package },
  SHIPPED: { label: 'Shipped', color: '#007AFF', bgColor: '#DBEAFE', Icon: Truck },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7', Icon: Check },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
};

const SHIPMENT_STATUS_CONFIG: Record<ShipmentStatus, { label: string; color: string; bgColor: string }> = {
  CREATED: { label: 'Created', color: '#F59E0B', bgColor: '#FEF3C7' },
  LABEL_PURCHASED: { label: 'Label Created', color: '#5856D6', bgColor: '#EDE9FE' },
  IN_TRANSIT: { label: 'In Transit', color: '#007AFF', bgColor: '#DBEAFE' },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7' },
  EXCEPTION: { label: 'Exception', color: '#EF4444', bgColor: '#FEE2E2' },
};

const PAYMENT_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  PENDING: { label: 'Pending', color: '#F59E0B', bgColor: '#FEF3C7' },
  AUTHORIZED: { label: 'Authorized', color: '#3B82F6', bgColor: '#DBEAFE' },
  CAPTURED: { label: 'Captured', color: '#22C55E', bgColor: '#DCFCE7' },
  FAILED: { label: 'Failed', color: '#DC2626', bgColor: '#FEE2E2' },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100] },
  REFUNDED: { label: 'Refunded', color: '#8B5CF6', bgColor: '#EDE9FE' },
  PARTIALLY_REFUNDED: { label: 'Partial Refund', color: '#8B5CF6', bgColor: '#EDE9FE' },
  VOIDED: { label: 'Voided', color: colors.gray[600], bgColor: colors.gray[100] },
};

const ITEM_SHIPPING_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  unshipped: { label: 'Unshipped', color: '#F59E0B', bgColor: '#FEF3C7' },
  created: { label: 'Pending', color: '#F59E0B', bgColor: '#FEF3C7' },
  label_purchased: { label: 'Label Created', color: '#5856D6', bgColor: '#EDE9FE' },
  in_transit: { label: 'In Transit', color: '#007AFF', bgColor: '#DBEAFE' },
  delivered: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7' },
  exception: { label: 'Exception', color: '#EF4444', bgColor: '#FEE2E2' },
};

function formatDate(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function SellerOrderDetailPage() {
  useDocumentTitle('Order Details');
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [showShipLabelModal, setShowShipLabelModal] = useState(false);
  const [showShipTrackingModal, setShowShipTrackingModal] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const data = await getOrder(orderId!);
      setOrder(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleDownloadPickList = async () => {
    if (!orderId) return;
    try {
      setIsDownloading(true);
      const blob = await downloadPickList(orderId);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download pick list');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCancelSale = async (sale: ItemSaleWithPayment) => {
    const itemName = sale.itemSnapshot?.name ?? 'this item';
    const ps = sale.paymentStatus || 'PENDING';
    let msg = `Cancel "${itemName}"?`;
    if (ps === 'AUTHORIZED') msg += ' The payment authorization will be voided.';
    else if (ps === 'CAPTURED') msg += ' A full refund will be issued.';
    if (!window.confirm(msg)) return;

    try {
      setCancellingId(sale.id);
      await cancelSale(sale.id);
      await loadOrder(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel sale');
    } finally {
      setCancellingId(null);
    }
  };

  if (isLoading) {
    return (
      <SellerLayout>
        <div style={styles.loading}>Loading order...</div>
      </SellerLayout>
    );
  }

  if (error || !order) {
    return (
      <SellerLayout>
        <div style={styles.error}>{error || 'Order not found'}</div>
      </SellerLayout>
    );
  }

  const statusConfig = STATUS_CONFIG[order.status];
  const shipments = order.shipments || [];
  const unshippedItems = order.itemSales?.filter((s: ItemSaleWithPayment) =>
    !s.shipmentId && s.status !== 'CANCELLED' && s.status !== 'VOIDED'
  ) || [];
  const isOpen = order.status === 'OPEN';
  const hasUnshippedItems = unshippedItems.length > 0 && !isOpen;

  const sales = order.itemSales || [];
  const hasFailedPayments = order.hasFailedPayments || sales.some((s: ItemSaleWithPayment) => s.paymentStatus === 'FAILED');
  const hasUncapturedNonCancelled = sales.some((s: ItemSaleWithPayment) =>
    s.paymentStatus !== 'CAPTURED' && s.status !== 'CANCELLED' && s.status !== 'VOIDED'
  );

  const itemTotal = sales.reduce((sum, s) => sum + s.winningBid, 0);
  const shippingTotal = sales.reduce((sum, s) => sum + (s.shippingCost || 0), 0);
  const taxTotal = sales.reduce((sum, s) => sum + (s.tax || 0), 0);
  const orderTotal = itemTotal + shippingTotal + taxTotal;

  return (
    <SellerLayout>
      <button onClick={() => navigate('/sell/orders')} style={styles.backButton}>
        <ArrowLeft size={20} />
        <span>Back to Orders</span>
      </button>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Order to {order.recipientName}</h1>
          <p style={styles.orderDate}>{formatDate(order.createdAt)}</p>
        </div>
        <div
          style={{
            ...styles.statusBadge,
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.color,
          }}
        >
          <statusConfig.Icon size={16} />
          <span>{statusConfig.label}</span>
        </div>
      </div>

      {hasFailedPayments && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '12px 16px', backgroundColor: '#FEF3C7', borderRadius: '10px',
          color: '#92400E', fontSize: '14px', fontWeight: 500, marginBottom: '16px',
        }}>
          <AlertTriangle size={18} />
          <span>This order has a failed payment. Order must be fixed before you can ship. Either the buyer needs to fix their payment or you can cancel any items with a failed payment.</span>
        </div>
      )}

      <div style={styles.grid}>
        <div style={styles.mainColumn}>
          {/* Shipping Address Card */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <MapPin size={16} />
              Shipping Address
            </h2>
            <div style={styles.addressBlock}>
              <p style={styles.addressName}>{order.recipientName}</p>
              <p style={styles.addressLine}>{order.streetLine1}</p>
              {order.streetLine2 && (
                <p style={styles.addressLine}>{order.streetLine2}</p>
              )}
              <p style={styles.addressLine}>
                {order.city}, {order.state} {order.zipCode}
              </p>
              {order.country && order.country !== 'US' && (
                <p style={styles.addressLine}>{order.country}</p>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <Package size={16} />
              Items ({order.itemSaleCount})
            </h2>
            <div style={styles.itemsTable}>
              <div style={styles.itemsHeader}>
                <span style={{ flex: 1 }}>Item</span>
                <span style={{ width: '100px' }}>Sale #</span>
                <span style={{ width: '100px', textAlign: 'center' }}>Payment</span>
                <span style={{ width: '100px', textAlign: 'center' }}>Fulfillment</span>
                <span style={{ width: '80px', textAlign: 'right' }}>Price</span>
                <span style={{ width: '80px', textAlign: 'right' }}>Shipping</span>
                <span style={{ width: '70px', textAlign: 'right' }}>Tax</span>
                <span style={{ width: '70px', textAlign: 'center' }}></span>
              </div>
              {order.itemSales?.map((sale: ItemSaleWithPayment) => {
                const shippingStatusConfig = ITEM_SHIPPING_STATUS_CONFIG[sale.shippingStatus || 'unshipped'];
                const isCancelled = sale.status === 'CANCELLED' || sale.status === 'VOIDED';
                const canCancel = !isCancelled && sale.status !== 'SHIPPED' && sale.status !== 'DELIVERED';
                return (
                  <div key={sale.id} style={{ ...styles.itemRow, ...(isCancelled ? { opacity: 0.5 } : {}) }}>
                    <div style={styles.itemInfo}>
                      {sale.itemSnapshot?.imageUrl ? (
                        <Img
                          src={sale.itemSnapshot.imageUrl}
                          alt={sale.itemSnapshot.name}
                          variant="thumb"
                          renderedWidth={48}
                          blurhash={sale.itemSnapshot.blurhash}
                          style={styles.itemImage}
                        />
                      ) : (
                        <div style={styles.itemImagePlaceholder}>
                          <Package size={20} color={colors.gray[300]} />
                        </div>
                      )}
                      <div>
                        <p style={styles.itemName}>{sale.itemSnapshot?.name ?? 'Unknown Item'}</p>
                        {sale.itemSnapshot?.description && (
                          <p style={styles.itemDescription}>{sale.itemSnapshot.description}</p>
                        )}
                      </div>
                    </div>
                    <span
                      style={styles.orderIdLink}
                      onClick={() => navigate(`/sell/sales/${sale.id}`)}
                    >
                      {sale.id}
                    </span>
                    <span style={{ width: '100px', textAlign: 'center' }}>
                      {(() => {
                        const ps = PAYMENT_STATUS_CONFIG[sale.paymentStatus || 'PENDING'];
                        return (
                          <span style={{
                            ...styles.itemShipBadge,
                            backgroundColor: ps.bgColor,
                            color: ps.color,
                          }}>
                            {ps.label}
                          </span>
                        );
                      })()}
                    </span>
                    <span style={{ width: '100px', textAlign: 'center' }}>
                      {sale.shipmentId ? (
                        <span
                          style={{
                            ...styles.itemShipBadge,
                            backgroundColor: shippingStatusConfig?.bgColor,
                            color: shippingStatusConfig?.color,
                            cursor: 'pointer',
                          }}
                          onClick={() => navigate(`/sell/shipments/${sale.shipmentId}`)}
                        >
                          {shippingStatusConfig?.label}
                        </span>
                      ) : (
                        <span style={{
                          ...styles.itemShipBadge,
                          backgroundColor: '#FEF3C7',
                          color: '#F59E0B',
                        }}>
                          Unshipped
                        </span>
                      )}
                    </span>
                    <span style={styles.itemPrice}>{formatCurrency(sale.winningBid)}</span>
                    <span style={styles.itemSecondaryAmount}>{formatCurrency(sale.shippingCost)}</span>
                    <span style={styles.itemTaxAmount}>{formatCurrency(sale.tax)}</span>
                    <span style={{ width: '70px', textAlign: 'center' }}>
                      {canCancel && (
                        <button
                          onClick={() => handleCancelSale(sale)}
                          disabled={cancellingId === sale.id}
                          style={styles.cancelButton}
                        >
                          {cancellingId === sale.id ? '...' : 'Cancel'}
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={styles.sideColumn}>
          {/* Actions Card — first */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Actions</h2>
            {hasUnshippedItems && (
              <button
                style={{
                  ...styles.primaryButton,
                  ...(hasUncapturedNonCancelled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                }}
                onClick={() => !hasUncapturedNonCancelled && setShowShipLabelModal(true)}
                disabled={hasUncapturedNonCancelled}
              >
                <Tag size={18} />
                Buy Shipping Label
              </button>
            )}
            {hasUnshippedItems && (
              <button
                style={{
                  ...styles.secondaryButton,
                  marginTop: '8px',
                  ...(hasUncapturedNonCancelled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                }}
                onClick={() => !hasUncapturedNonCancelled && setShowShipTrackingModal(true)}
                disabled={hasUncapturedNonCancelled}
              >
                <Truck size={18} />
                Add Tracking
              </button>
            )}
            <button
              style={{ ...styles.secondaryButton, marginTop: '8px', marginBottom: 0 }}
              onClick={handleDownloadPickList}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Downloading...
                </>
              ) : (
                <>
                  <FileText size={18} />
                  Download Pick List
                </>
              )}
            </button>
          </div>

          {/* Summary Card */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Summary</h2>
            <div style={styles.summaryRow}>
              <span>Show Name</span>
              <span>{order.showName || 'N/A'}</span>
            </div>
            {order.showDate && (
              <div style={styles.summaryRow}>
                <span>Show Date</span>
                <span>{formatDate(order.showDate)}</span>
              </div>
            )}
            <div style={styles.divider} />
            <div style={styles.summaryRow}>
              <span>Item Total ({order.itemSaleCount} item{order.itemSaleCount !== 1 ? 's' : ''})</span>
              <span>{formatCurrency(itemTotal)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Shipping Total</span>
              <span>{formatCurrency(shippingTotal)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Tax Total</span>
              <span>{formatCurrency(taxTotal)}</span>
            </div>
            <div style={styles.divider} />
            <div style={{ ...styles.summaryRow, marginBottom: 0 }}>
              <span style={styles.totalLabel}>Total</span>
              <span style={styles.totalValue}>{formatCurrency(orderTotal)}</span>
            </div>
          </div>

          {/* Shipments Card */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <Truck size={16} />
              Shipments ({shipments.length})
            </h2>

            {shipments.length === 0 ? (
              <p style={styles.noTrackingText}>No shipments created yet</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {shipments.map((shipment: Shipment) => {
                  const sc = SHIPMENT_STATUS_CONFIG[shipment.status];
                  return (
                    <div
                      key={shipment.id}
                      style={styles.shipmentCard}
                      onClick={() => navigate(`/sell/shipments/${shipment.id}`)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', fontFamily: 'monospace', color: colors.brand.blue }}>{shipment.id}</span>
                        <span style={{ ...styles.itemShipBadge, backgroundColor: sc.bgColor, color: sc.color }}>
                          {sc.label}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: colors.gray[600] }}>
                        {shipment.itemSaleCount} item{shipment.itemSaleCount !== 1 ? 's' : ''}
                        {shipment.carrier && shipment.trackingNumber && (
                          <> &middot; {shipment.carrier}</>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      <ShipLabelModal
        isOpen={showShipLabelModal}
        onClose={() => setShowShipLabelModal(false)}
        onComplete={() => loadOrder(true)}
        orderId={orderId!}
        unshippedItems={unshippedItems}
        initialWeightOz={shipments.at(-1)?.weightOz?.toString() || ''}
        initialLengthIn={shipments.at(-1)?.lengthIn?.toString() || ''}
        initialWidthIn={shipments.at(-1)?.widthIn?.toString() || ''}
        initialHeightIn={shipments.at(-1)?.heightIn?.toString() || ''}
      />

      <ShipTrackingModal
        isOpen={showShipTrackingModal}
        onClose={() => setShowShipTrackingModal(false)}
        onComplete={() => loadOrder(true)}
        orderId={orderId!}
        unshippedItems={unshippedItems}
      />
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: colors.gray[600],
  },
  error: {
    textAlign: 'center',
    padding: '48px',
    color: colors.accent.pink,
    backgroundColor: '#FEE2E2',
    borderRadius: '12px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 4px 0',
  },
  orderDate: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: 0,
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 600,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: '20px',
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '20px',
    border: `1px solid ${colors.gray[300]}`,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 16px 0',
  },
  addressBlock: {
    lineHeight: 1.6,
  },
  addressName: {
    fontSize: '16px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: '0 0 4px 0',
  },
  addressLine: {
    fontSize: '14px',
    color: colors.gray[700],
    margin: 0,
  },
  itemsTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemsHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${colors.gray[200]}`,
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  itemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  itemImage: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    objectFit: 'cover',
  },
  itemImagePlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemName: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.ink.black,
    margin: 0,
  },
  itemDescription: {
    fontSize: '12px',
    color: colors.gray[600],
    margin: '2px 0 0 0',
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  orderId: {
    width: '100px',
    fontSize: '13px',
    color: colors.gray[600],
    fontFamily: 'monospace',
  },
  orderIdLink: {
    width: '100px',
    fontSize: '13px',
    color: colors.brand.blue,
    fontFamily: 'monospace',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  itemPrice: {
    width: '80px',
    textAlign: 'right',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.ink.black,
  },
  itemSecondaryAmount: {
    width: '80px',
    textAlign: 'right',
    fontSize: '13px',
    color: colors.gray[600],
  },
  itemTaxAmount: {
    width: '70px',
    textAlign: 'right',
    fontSize: '13px',
    color: colors.gray[600],
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: colors.gray[700],
    marginBottom: '12px',
  },
  totalLabel: {
    fontWeight: 600,
    color: colors.ink.black,
  },
  totalValue: {
    fontWeight: 700,
    fontSize: '16px',
    color: colors.ink.black,
  },
  divider: {
    height: '1px',
    backgroundColor: colors.gray[300],
    margin: '12px 0',
  },
  noTrackingText: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: '0 0 12px 0',
  },
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  itemShipBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '100px',
    fontSize: '11px',
    fontWeight: 600,
  },
  shipmentCard: {
    padding: '12px',
    backgroundColor: colors.gray[100],
    borderRadius: '10px',
    cursor: 'pointer',
    border: `1px solid ${colors.gray[200]}`,
  },
  cancelButton: {
    padding: '3px 8px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#DC2626',
    backgroundColor: 'transparent',
    border: '1px solid #FCA5A5',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};
